import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { getDashboard, postMessage, updateGoal, generateGoal } from "@/lib/api";
import SoftImage from "@/components/SoftImage";
import { StatusChip } from "@/components/dashboard/ui";
import {
  Users, Calendar, FileText, MessageSquare, BarChart3, TrendingUp, TrendingDown, ArrowUpRight,
  ChevronRight, MoreHorizontal, Brain, Pill, CheckCircle2, Circle, Video, Mic, PhoneOff, Monitor,
  Camera, Timer, PenTool, Smile, Gamepad2, Star, MapPin, UsersRound, Sparkles, Play, Send, Paperclip,
  Moon, Activity, Home, School, AlertTriangle, TrendingUp as Trend, PartyPopper, Check
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const KPI_ICONS = { users: Users, calendar: Calendar, file: FileText, chat: MessageSquare, chart: BarChart3 };
const KPI_TINT = { users: ["#e8f1ff", "#1a5fd0"], calendar: ["#e6f7ec", "#2f9e57"], file: ["#eef0ff", "#5a67e0"], chat: ["#e8f1ff", "#1a5fd0"], chart: ["#f0eaff", "#7c53d6"] };

function Widget({ title, icon: Icon, tint = "#1a5fd0", right, children, className = "" }) {
  return (
    <div className={`card-soft p-5 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          {Icon && <span className="h-8 w-8 rounded-xl grid place-items-center" style={{ background: `${tint}18`, color: tint }}><Icon size={17} /></span>}
          <h3 className="font-display text-[17px] font-bold text-[#10233f]">{title}</h3>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

const ViewAll = ({ to = "#", label = "View All" }) => (
  <Link to={to} className="flex items-center gap-1 text-[13px] font-semibold text-[#1a5fd0] hover:gap-1.5 transition-all">{label} <ChevronRight size={15} /></Link>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [planTab, setPlanTab] = useState("Goals");
  const [msg, setMsg] = useState("");
  const [messages, setMessages] = useState([]);
  const [goals, setGoals] = useState([]);

  const load = async () => {
    const data = await getDashboard();
    setD(data); setMessages(data.messages); setGoals(data.goals);
  };
  useEffect(() => { load().catch(() => toast.error("Failed to load dashboard")); }, []);

  if (!d) return <div className="grid place-items-center py-32 text-slate-400">Loading your dashboard…</div>;

  const p = d.featured_patient;
  const dotColor = { blue: "#1a5fd0", green: "#2f9e57", orange: "#e0842f", purple: "#7c53d6" };

  const sendMsg = async () => {
    if (!msg.trim()) return;
    const text = msg; setMsg("");
    try { const m = await postMessage({ text }); setMessages((prev) => [...prev, m]); }
    catch { toast.error("Could not send message"); }
  };
  const toggleGoal = async (g) => {
    const done = !g.done;
    setGoals((prev) => prev.map((x) => x.id === g.id ? { ...x, done } : x));
    try { await updateGoal(g.id, { done, status: done ? "On Track" : g.status }); } catch {}
  };
  const aiGenerate = async () => {
    try { const g = await generateGoal(); setGoals((prev) => [...prev, g]); toast.success("AI added a new goal suggestion"); }
    catch { toast.error("Generation failed"); }
  };

  return (
    <div className="fade-in space-y-5">
      {/* greeting */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-[26px] font-extrabold text-[#10233f]">{d.greeting} <span className="align-middle">👋</span></h1>
          <p className="text-slate-500">Today you're helping build brighter futures.</p>
        </div>
        <div className="flex items-center gap-2.5 text-right">
          <span className="h-11 w-11 rounded-2xl bg-[#e8f1ff] grid place-items-center text-[#1a5fd0]"><Calendar size={20} /></span>
          <div><div className="text-[15px] font-bold text-[#10233f]">{d.date}</div><div className="text-[13px] text-slate-500">Here for brighter tomorrows.</div></div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {d.kpis.map((k) => {
          const Ic = KPI_ICONS[k.icon]; const [bg, fg] = KPI_TINT[k.icon];
          const up = k.dir === "up";
          return (
            <div key={k.id} className="card-soft card-hover p-4 relative">
              <div className="flex items-start justify-between">
                <span className="h-11 w-11 rounded-2xl grid place-items-center" style={{ background: bg, color: fg }}><Ic size={21} /></span>
                {k.badge && <span className="absolute top-3 right-3 h-5 min-w-5 px-1 grid place-items-center rounded-full bg-[#e23b3b] text-white text-[11px] font-bold">{k.badge}</span>}
              </div>
              <div className="mt-3 flex items-end justify-between">
                <div>
                  <div className="font-display text-[26px] font-extrabold text-[#10233f] leading-none">{k.value}</div>
                  <div className="text-[13px] text-slate-500 mt-1">{k.label}</div>
                </div>
                {k.sparkline ? (
                  <svg width="52" height="26" viewBox="0 0 52 26" className="shrink-0"><polyline fill="none" stroke="#2f9e57" strokeWidth="2.5" strokeLinecap="round" points="2,20 12,16 20,18 30,9 40,11 50,3" /></svg>
                ) : k.change ? (
                  <span className={`flex items-center gap-0.5 text-[13px] font-bold ${up ? "text-[#2f9e57]" : "text-[#e0842f]"}`}>{up ? <TrendingUp size={15} /> : <TrendingDown size={15} />}{k.change}%</span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-[1.35fr_1.15fr_0.95fr_1.05fr] gap-5">
        {/* Featured Patient */}
        <Widget title="Featured Patient" right={<ViewAll to="/patients" label="View All Patients" />}>
          <div className="flex gap-4">
            <SoftImage src={p.photo} alt={p.name} variant="none" rounded className="h-24 w-24 shrink-0 rounded-2xl" imgClassName="rounded-2xl" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="font-display text-[19px] font-extrabold text-[#10233f]">{p.name}</h4>
                <StatusChip status={p.status} />
                <button className="ml-auto text-slate-300 hover:text-slate-500"><MoreHorizontal size={18} /></button>
              </div>
              <p className="text-[13px] text-slate-500">Age {p.age} · DOB {p.dob}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">{p.tags.map((t) => <span key={t} className="chip bg-[#eef5ff] text-[#1a5fd0]">{t}</span>)}</div>
            </div>
          </div>
          <div className="font-hand text-[19px] text-[#1a5fd0] mt-2 flex items-center gap-1.5">"{p.quote}" <span className="text-[#37a862]">♥</span></div>
          <div className="mt-3 flex gap-1 border-b border-slate-100">
            {["Overview", "EHR", "Assessments", "Treatment Plan", "Progress"].map((t) => (
              <button key={t} onClick={() => setTab(t)} className={`px-2.5 py-2 text-[13px] font-semibold border-b-2 -mb-px transition-colors ${tab === t ? "border-[#1a5fd0] text-[#1a5fd0]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>{t}</button>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex gap-2.5"><Brain size={17} className="text-[#1a5fd0] mt-0.5" /><div><div className="text-[12px] text-slate-400 font-medium">Primary Diagnosis</div><div className="text-[13.5px] font-semibold text-[#10233f] leading-snug">{p.primary_diagnosis}</div></div></div>
              <div className="flex gap-2.5"><Pill size={17} className="text-[#1a5fd0] mt-0.5" /><div><div className="text-[12px] text-slate-400 font-medium">Medications</div><div className="text-[13.5px] font-semibold text-[#10233f]">{p.medications}</div></div></div>
              <div className="flex gap-2.5"><UsersRound size={17} className="text-[#1a5fd0] mt-0.5" /><div><div className="text-[12px] text-slate-400 font-medium">Care Team</div><div className="flex -space-x-2 mt-1">{p.care_team.slice(0, 4).map((a, i) => <img key={i} src={a} alt="care" className="h-7 w-7 rounded-full ring-2 ring-white object-cover" />)}<span className="h-7 w-7 rounded-full bg-[#e8f1ff] text-[#1a5fd0] text-[11px] font-bold grid place-items-center ring-2 ring-white">+2</span></div></div></div>
              <div className="flex gap-2.5"><Calendar size={17} className="text-[#1a5fd0] mt-0.5" /><div className="flex-1"><div className="text-[12px] text-slate-400 font-medium">Next Appointment</div><div className="flex items-center gap-2"><div className="text-[13px] font-semibold text-[#10233f]">{p.next_appointment?.when} ({p.next_appointment?.type})</div><Link to="/telehealth" className="btn-primary px-3 py-1 text-[12px] font-bold">Join</Link></div></div></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2"><span className="text-[13px] font-bold text-[#10233f]">Developmental Milestones</span><ViewAll /></div>
              <ul className="space-y-2.5">
                {p.milestones.map((m, i) => (
                  <li key={i} className="flex items-center gap-2">
                    {m.status === "On track" ? <CheckCircle2 size={17} className="text-[#2f9e57]" /> : <Circle size={17} className="text-slate-300" />}
                    <span className="text-[13px] text-[#33425c] flex-1">{m.label}</span>
                    <span className={`text-[11px] font-semibold ${m.status === "On track" ? "text-[#2f9e57]" : "text-[#e0842f]"}`}>{m.status}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Widget>

        {/* Telehealth */}
        <Widget title="Telehealth Session" icon={Video} right={<span className="flex items-center gap-1.5 text-[12px] font-bold text-[#2f9e57]"><span className="h-2 w-2 rounded-full bg-[#2f9e57] live-dot" /> Live Now</span>}>
          <div className="relative rounded-2xl overflow-hidden">
            <img src={p.photo} alt="session" className="w-full h-[188px] object-cover" />
            <div className="absolute top-3 right-3 w-20 h-24 rounded-xl overflow-hidden ring-2 ring-white shadow-lg"><img src="/generated/clinician_female.png" alt="clinician" className="w-full h-full object-cover" /><span className="absolute bottom-1 left-1 text-[10px] font-bold text-white bg-black/40 px-1.5 rounded">You</span></div>
            <div className="absolute bottom-3 right-3 bg-white/95 rounded-full px-2.5 py-1 text-[12px] font-bold text-[#10233f] flex items-center gap-1 shadow"><Star size={12} className="fill-[#e8b530] text-[#e8b530]" /> Great job, Noah!</div>
          </div>
          <div className="mt-3 flex items-center justify-center gap-2.5">
            {[[Mic, "#eef5ff", "#1a5fd0"], [Video, "#eef5ff", "#1a5fd0"], [PhoneOff, "#e23b3b", "#fff"], [Monitor, "#eef5ff", "#1a5fd0"], [Camera, "#eef5ff", "#1a5fd0"], [Timer, "#eef5ff", "#1a5fd0"]].map(([Ic, bg, fg], i) => (
              <button key={i} className="h-10 w-10 rounded-full grid place-items-center" style={{ background: bg, color: fg }}><Ic size={18} /></button>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-5 gap-1">
            {[[PenTool, "Whiteboard", "#1a5fd0"], [Smile, "Puppets", "#e0842f"], [Smile, "Emoji", "#e8b530"], [Gamepad2, "Games", "#7c53d6"], [Timer, "Timer", "#2f9e57"]].map(([Ic, label, c], i) => (
              <button key={i} className="flex flex-col items-center gap-1 py-2 rounded-xl hover:bg-[#f4f8fe]"><span className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: `${c}18`, color: c }}><Ic size={17} /></span><span className="text-[11px] font-semibold text-slate-500">{label}</span></button>
            ))}
          </div>
        </Widget>

        {/* Schedule */}
        <Widget title="Today's Schedule" icon={Calendar} right={<ViewAll to="/schedule" label="View Calendar" />}>
          <ul className="space-y-1">
            {d.schedule.map((s, i) => (
              <li key={s.id} className="flex gap-3">
                <div className="flex flex-col items-center pt-1">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: dotColor[s.color] }} />
                  {i < d.schedule.length - 1 && <span className="w-px flex-1 bg-slate-100 my-1" />}
                </div>
                <div className="flex-1 pb-3">
                  <div className="text-[12px] font-bold text-slate-400">{s.time}</div>
                  <div className="text-[13.5px] font-bold text-[#10233f] leading-tight">{s.patient}</div>
                  <div className="text-[12px] text-slate-500">{s.type}{s.subtitle ? ` · ${s.subtitle}` : ""}</div>
                </div>
                <div className="pt-4">
                  {s.action === "Join" ? <Link to="/telehealth" className="btn-primary px-3 py-1 text-[12px] font-bold inline-flex items-center gap-1"><Video size={12} /> Join</Link>
                    : s.mode === "location" ? <span className="flex items-center gap-1 text-[12px] font-semibold text-slate-500"><MapPin size={12} /> {s.location}</span>
                      : s.mode === "group" ? <span className="flex items-center gap-1 text-[12px] font-semibold text-[#7c53d6]"><UsersRound size={12} /> {s.location}</span>
                        : <span className="flex items-center gap-1 text-[12px] font-semibold text-[#7c53d6]"><Video size={12} /> {s.location}</span>}
                </div>
              </li>
            ))}
          </ul>
        </Widget>

        {/* Care Team Chat */}
        <Widget title="Care Team Chat" icon={MessageSquare} right={<ViewAll to="/messages" />}>
          <div className="space-y-3 max-h-[260px] overflow-y-auto thin-scroll pr-1">
            {messages.map((m) => (
              <div key={m.id} className={`flex gap-2.5 ${m.self ? "flex-row-reverse text-right" : ""}`}>
                <img src={m.avatar} alt={m.author} className="h-8 w-8 rounded-full object-cover shrink-0" />
                <div className="min-w-0">
                  <div className={`flex items-center gap-1.5 ${m.self ? "justify-end" : ""}`}><span className="text-[12.5px] font-bold text-[#10233f]">{m.author}</span><span className="text-[10.5px] text-slate-400">{m.role}</span><span className="text-[10.5px] text-slate-300">{m.time}</span></div>
                  <div className={`mt-1 text-[13px] text-[#33425c] rounded-2xl px-3 py-2 inline-block ${m.self ? "bg-[#1a5fd0] text-white" : "bg-[#f2f6fc]"}`}>{m.text}</div>
                  {m.reactions?.length > 0 && <div className="mt-1 flex gap-1">{m.reactions.map((r, i) => <span key={i} className="text-[11px] bg-white border border-slate-100 rounded-full px-1.5">{r.emoji} {r.count}</span>)}</div>}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2 bg-[#f2f6fc] rounded-full px-3 py-1.5">
            <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} data-testid="chat-input" placeholder="Type a message…" className="flex-1 bg-transparent outline-none text-[13.5px]" />
            <button className="text-slate-400"><Paperclip size={16} /></button>
            <button onClick={sendMsg} data-testid="chat-send" className="h-8 w-8 rounded-full btn-primary grid place-items-center"><Send size={15} /></button>
          </div>
        </Widget>
      </div>

      {/* Row 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-[1.4fr_1fr_1fr_1fr] gap-5">
        {/* Treatment Planner */}
        <Widget title="AI-Assisted Treatment Planner" icon={Sparkles} tint="#7c53d6" right={<div className="flex items-center gap-2"><button onClick={aiGenerate} data-testid="ai-generate" className="flex items-center gap-1.5 text-[12.5px] font-semibold text-[#1a5fd0] bg-[#eef5ff] rounded-full px-3 py-1.5 hover:bg-[#e0ecff]"><Sparkles size={14} /> Generate with AI</button><MoreHorizontal size={18} className="text-slate-300" /></div>}>
          <div className="flex gap-1 border-b border-slate-100 mb-3 overflow-x-auto">
            {["Goals", "Interventions", "Home Activities", "School Goals", "Routines"].map((t) => (
              <button key={t} onClick={() => setPlanTab(t)} className={`px-2.5 py-2 text-[13px] font-semibold whitespace-nowrap border-b-2 -mb-px ${planTab === t ? "border-[#1a5fd0] text-[#1a5fd0]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>{t}</button>
            ))}
          </div>
          <ul className="space-y-2.5">
            {goals.map((g) => (
              <li key={g.id} className="flex items-start gap-3">
                <button onClick={() => toggleGoal(g)} data-testid={`goal-${g.id}`} className={`mt-0.5 h-5 w-5 rounded-md grid place-items-center border ${g.done ? "bg-[#1a5fd0] border-[#1a5fd0] text-white" : "border-slate-300 text-transparent hover:border-[#1a5fd0]"}`}><Check size={13} /></button>
                <div className="flex-1"><div className="text-[14px] font-bold text-[#10233f]">{g.title}</div><div className="text-[12.5px] text-slate-500">{g.detail}</div></div>
                <StatusChip status={g.status} />
              </li>
            ))}
          </ul>
        </Widget>

        {/* Parent Coaching Hub */}
        <Widget title="Parent Coaching Hub" icon={UsersRound} tint="#2f9e57" right={<ViewAll to="/app-resources" label="View Library" />}>
          <div className="space-y-3">
            {d.coaching.map((c) => (
              <div key={c.id} className="flex gap-3 group cursor-pointer">
                <div className="relative h-12 w-16 rounded-xl overflow-hidden shrink-0"><img src={c.thumb} alt={c.title} className="w-full h-full object-cover" /><span className="absolute inset-0 grid place-items-center bg-black/20 group-hover:bg-black/30"><Play size={16} className="text-white fill-white" /></span></div>
                <div className="min-w-0"><div className="text-[13.5px] font-bold text-[#10233f] leading-tight line-clamp-2">{c.title}</div><div className="text-[12px] text-slate-500 mt-0.5">{c.kind} · {c.duration}</div></div>
              </div>
            ))}
          </div>
        </Widget>

        {/* Assessments */}
        <Widget title="Assessments & Screeners" icon={BarChart3} tint="#7c53d6" right={<ViewAll to="/assessments" />}>
          <div className="space-y-3">
            {d.assessments.map((a) => (
              <div key={a.id} className="flex items-center gap-2.5">
                <span className="h-8 w-8 rounded-lg bg-[#eef5ff] grid place-items-center text-[#1a5fd0] shrink-0"><FileText size={15} /></span>
                <div className="flex-1 min-w-0"><div className="text-[13.5px] font-bold text-[#10233f]">{a.name} <span className="text-slate-400 font-medium">({a.category})</span></div><div className="text-[11.5px] text-slate-400">{a.date}</div></div>
                <div className="flex flex-col items-end gap-1"><span className="flex items-center gap-1 text-[11px] font-semibold text-[#2f9e57]"><CheckCircle2 size={12} /> {a.status}</span><StatusChip status={a.severity} /></div>
              </div>
            ))}
          </div>
        </Widget>

        {/* Trends */}
        <Widget title="Mood & Behavior Trends" icon={Activity} tint="#2f9e57" right={<span className="text-[12px] font-semibold text-slate-400">Last 2 Weeks ▾</span>}>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {[[Moon, "Sleep", d.trends.sleep, "#1a5fd0"], [Smile, "Anxiety", d.trends.anxiety, "#2f9e57"], [BarChart3, "Behavior", d.trends.behavior, "#7c53d6"]].map(([Ic, label, t, c], i) => (
              <div key={i} className="rounded-xl bg-[#f6f9fe] p-2 text-center"><Ic size={15} className="mx-auto" style={{ color: c }} /><div className="text-[15px] font-extrabold text-[#10233f] mt-1">{t.value}{t.unit}</div><div className="text-[10.5px] text-slate-400">{label}</div><div className={`text-[10.5px] font-bold ${t.dir === "up" ? "text-[#2f9e57]" : "text-[#e0842f]"}`}>{t.dir === "up" ? "↑" : "↓"} {t.change}%</div></div>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={d.trends.series} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f8" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#9aa7bd" }} axisLine={false} tickLine={false} interval={1} />
              <YAxis domain={[0, 5]} tick={{ fontSize: 9, fill: "#9aa7bd" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #eef2f8", fontSize: 12 }} />
              <Line type="monotone" dataKey="sleep" stroke="#1a5fd0" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="anxiety" stroke="#7c53d6" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="behavior" stroke="#2f9e57" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
          <div className="flex gap-3 justify-center mt-1 text-[11px] text-slate-500">
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#1a5fd0]" /> Sleep</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#7c53d6]" /> Anxiety</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#2f9e57]" /> Behavior</span>
          </div>
        </Widget>
      </div>

      {/* Row 4 */}
      <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_1.4fr_0.9fr] gap-5">
        <Widget title="School & IEP Coordinator" icon={School} right={<ViewAll to="/school-iep" label="View IEP" />}>
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1"><span className="text-[13px] font-bold text-[#10233f]">IEP Goals</span><span className="text-[12px] font-semibold text-slate-500">{d.iep.goals_on_track} of {d.iep.goals_total} on track</span></div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#2f7bf0] to-[#1a5fd0]" style={{ width: `${(d.iep.goals_on_track / d.iep.goals_total) * 100}%` }} /></div>
          </div>
          <div className="text-[12px] font-bold text-slate-400 mb-2">Recent Updates</div>
          <ul className="space-y-2">{d.iep.updates.map((u, i) => (<li key={i} className="flex gap-2.5 text-[13px]"><span className="text-[11px] font-bold text-[#1a5fd0] w-11 shrink-0">{u.date}</span><span className="text-[#33425c]">{u.text}</span></li>))}</ul>
        </Widget>

        <Widget title="AI Insights & Alerts" icon={Sparkles} tint="#1a5fd0" right={<ViewAll to="/analytics" />}>
          <div className="grid md:grid-cols-3 gap-3">
            {d.insights.map((ins) => {
              const cfg = { risk: [AlertTriangle, "#e23b3b", "#fdecec"], success: [PartyPopper, "#2f9e57", "#e6f7ec"], trend: [Trend, "#1a5fd0", "#e8f1ff"] }[ins.type];
              const [Ic, c, bg] = cfg;
              return (
                <div key={ins.id} className="rounded-2xl p-3.5" style={{ background: bg }}>
                  <div className="flex items-center gap-1.5 mb-1"><Ic size={15} style={{ color: c }} /><span className="text-[13px] font-bold" style={{ color: c }}>{ins.title}</span></div>
                  <p className="text-[12px] text-[#33425c] leading-snug">{ins.detail}</p>
                  {ins.time && <p className="text-[11px] text-slate-400 mt-1.5">{ins.time}</p>}
                </div>
              );
            })}
          </div>
        </Widget>

        <div className="card-soft p-5 relative overflow-hidden bg-gradient-to-br from-[#eef6ff] to-white flex flex-col items-center justify-center text-center">
          <svg className="absolute right-3 top-3 opacity-90" width="42" height="42" viewBox="0 0 64 64" fill="none" stroke="#f2b21a" strokeWidth="3" strokeLinecap="round"><circle cx="32" cy="32" r="10" />{[...Array(8)].map((_, i) => { const a = (i * Math.PI) / 4; return <line key={i} x1={32 + Math.cos(a) * 15} y1={32 + Math.sin(a) * 15} x2={32 + Math.cos(a) * 22} y2={32 + Math.sin(a) * 22} />; })}</svg>
          <div className="font-display text-[17px] font-extrabold text-[#1a5fd0] tracking-wide">KINDER MINDS<br />BRIGHTER FUTURES</div>
          <p className="mt-2 text-[12.5px] text-slate-500 max-w-[220px]">Thank you for the difference you make every day. ♥</p>
        </div>
      </div>
    </div>
  );
}
